import styled from 'styled-components';

import Section from '@/components/templates/doctor/detail/components/Section/Section';
import {
  DoctorDetailTabType,
  DoctorDetailViewModel,
} from '@/components/templates/doctor/detail/useDoctorDetailViewModel';
import { formatPriceComma } from '@/utils/format';
import { CheckItem, Toggle } from 'doctornow-design-system';

// eslint-disable-next-line import/order
import CategoryHeader from '../CategoryHeader/CategoryHeader';
import PointTermInfo from './PointTermInfo';

interface Props {
  doctorDetailViewModel: DoctorDetailViewModel;
}

const NonBenefitPrice = ({ doctorDetailViewModel }: Props) => {
  return (
    <>
      <AnchorArea id={DoctorDetailTabType.NonBenefitPrice} />

      <div id={DoctorDetailTabType.NonBenefitPrice}>
        <Section>
          <Section.Title title={'비급여 진료비'}>
            {doctorDetailViewModel.isF2FDiagnosis() && (
              <div className='flex flex-1 items-center justify-end gap-2'>
                <CheckItem
                  label='원내처방'
                  checkboxProps={{
                    checked: doctorDetailViewModel.isWithDispensation,
                    onCheckedChange: doctorDetailViewModel.toggleWithDispensation,
                  }}
                />
              </div>
            )}
          </Section.Title>
          <div className='w-full px-4'>
            {doctorDetailViewModel.isLoggedIn && (
              <div className='flex w-full items-center justify-between rounded-lg bg-gray-20 p-4'>
                <div className='flex items-center gap-1'>
                  <img className='h-5 w-5' alt={'포인트 아이콘'} src={'/images/point/point.png'} />
                  <span className='typography-ui-15m text-gray-700'> 포인트 사용시</span>
                </div>
                <Toggle
                  checked={doctorDetailViewModel.isPointPriceVisible}
                  onChange={doctorDetailViewModel.togglePointPrice}
                />
              </div>
            )}
          </div>
          {doctorDetailViewModel.shouldShowNonBenefitCategoryHeader() && (
            <CategoryHeader>
              {doctorDetailViewModel.getNonBenefitPriceCategories().map((category) => (
                <CategoryHeader.Item
                  key={`non-benefit-price-category-${category.id}`}
                  isSelected={doctorDetailViewModel.isCategorySelected(category)}
                  onClick={() => doctorDetailViewModel.selectNonBenefitPriceCategory(category)}
                >
                  {category.name}
                </CategoryHeader.Item>
              ))}
            </CategoryHeader>
          )}
          <TreatmentPriceList>
            {doctorDetailViewModel.getNonBenefitTreatmentPriceList().map((treatment) => (
              <SameCategoryWrapper key={`non-benefit-treatment-${treatment.id}`}>
                {treatment.medicines.map((medicine) => (
                  <TreatmentPriceItem key={`non-benefit-treatment-${treatment.id}-${medicine.id}`}>
                    <TreatmentPriceItemMedicineLabel>
                      {`${medicine.treatmentMedicineName}, ${medicine.unitDisplayName}${medicine.unitTypeDisplayName}`}
                    </TreatmentPriceItemMedicineLabel>
                    <TreatmentPriceItemPriceLabel>
                      {formatPriceComma(
                        doctorDetailViewModel.isPointPriceVisible ? medicine.priceOnUsingPoint : medicine.originalPrice
                      )}
                      원
                    </TreatmentPriceItemPriceLabel>
                  </TreatmentPriceItem>
                ))}
              </SameCategoryWrapper>
            ))}
          </TreatmentPriceList>
          {doctorDetailViewModel.shouldShowPointTermInfo() && <PointTermInfo />}
        </Section>
        <Section.Divider />
      </div>
    </>
  );
};

export default NonBenefitPrice;

const SameCategoryWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
`;

const TreatmentPriceList = styled.div`
  width: 100%;

  ${SameCategoryWrapper}:not(:last-child) {
    border-bottom-width: 1px;
    border-bottom-color: ${({ theme }) => theme.color.gray40};
    border-bottom-style: solid;
  }

  ${SameCategoryWrapper}:not(:first-child) {
    padding-top: 16px;
  }
`;

const TreatmentPriceItem = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0px 24px;
`;

const TreatmentPriceItemMedicineLabel = styled.div`
  ${({ theme }) => `
        ${theme.typography.UI14M};
        color:${theme.color.gray700};
    `}
`;

const TreatmentPriceItemPriceLabel = styled.div`
  ${({ theme }) => `
        ${theme.typography.UI14M};
        color:${theme.color.primary700};
    `}
`;

const AnchorArea = styled.div`
  height: 108px;
  margin-top: -108px;
  display: block;
  visibility: hidden;
`;
